import React, { useEffect, useRef, lazy } from 'react';
import { AppState, LureType, ArmatureSize, LureSize } from '../types';
import { getArmatureSwing, getJigDisplacement } from '../utils/movement';
import { LureSelector } from './LureSelector';
const LURE_COLORS = ['#7fff00', '#d94a38', '#e8ecf0', '#b8c0c8', '#6b4423', '#ff6b00', '#ffff00', '#00ff00', '#ff1493', '#9400d3', '#000000', '#4169e1' // Blue
];
interface Props {
  state: AppState;
  onLureColorChange?: (color: string) => void;
  onLureSizeChange?: (size: LureSize) => void;
  onArmatureSizeChange?: (size: ArmatureSize) => void;
  onPauseCountdownChange?: (countdown: number) => void;
  onBobberStopDepthChange?: (depth: number) => void;
  zoomLevel?: number;
  instanceId?: string;
  lureSelectorPosition?: 'top' | 'bottom';
  presets?: (AppState | null)[];
  activePreset?: number | null;
  onPresetPlay?: (index: number) => void;
  onPresetSave?: (index: number) => void;
}
const ARMATURE_SIZES: ArmatureSize[] = ['XS', 'S', 'M', 'L', 'XL', 'XXL'];
export function WaterScene({
  state,
  onLureColorChange,
  onLureSizeChange,
  onArmatureSizeChange,
  onPauseCountdownChange,
  onBobberStopDepthChange,
  zoomLevel = 1,
  instanceId = 'main',
  lureSelectorPosition = 'bottom',
  presets,
  activePreset,
  onPresetPlay,
  onPresetSave
}: Props) {
  // Calculate lure scale multiplier based on size
  const getLureScale = (size: LureSize): number => {
    const scales: Record<LureSize, number> = {
      XS: 0.6,
      S: 0.8,
      M: 1.0,
      L: 1.3,
      XL: 1.6
    };
    return scales[size];
  };
  const lureScale = getLureScale(state.lureSize);
  const requestRef = useRef<number>();
  const timeRef = useRef<number>(0);
  const lastFrameRef = useRef<number>(0);
  // SVG Element Refs for high-performance updates
  const swingGroupRef = useRef<SVGGElement>(null);
  const lureTailRef = useRef<SVGPathElement>(null);
  const lureBodyRef = useRef<SVGGElement>(null);
  const bodyPathRef = useRef<SVGPathElement>(null);
  const pectoralTopRef = useRef<SVGPathElement>(null);
  const pectoralBotRef = useRef<SVGPathElement>(null);
  const dorsalRef = useRef<SVGPathElement>(null);
  const tailForkRef = useRef<SVGPathElement>(null);
  const fishingLineRef = useRef<SVGPathElement>(null);
  const bobberToArmLineRef = useRef<SVGLineElement>(null);
  const armatureLineRef = useRef<SVGLineElement>(null);
  const armatureTipDotRef = useRef<SVGCircleElement>(null);
  const wave1Ref = useRef<SVGPathElement>(null);
  const wave2Ref = useRef<SVGPathElement>(null);
  // Lure direction tracking — only flip when armature stops
  const prevSwingAngleRef = useRef<number>(0);
  const lastSwingSignRef = useRef<number>(1);
  const lureDirectionRef = useRef<number>(1);
  // Physics-based lure position in WORLD space
  const lureWxRef = useRef<number>(0);
  const lureWyRef = useRef<number>(0);
  const lureInitializedRef = useRef<boolean>(false);
  const prevLineLengthRef = useRef<number>(0);
  const prevLureWxRef = useRef<number>(0);
  const lureVxRef = useRef<number>(0); // horizontal velocity (momentum)
  const prevTipWxRef = useRef<number>(0); // previous armature tip X for direction tracking
  const prevTipWyRef = useRef<number>(0); // previous armature tip Y for direction tracking
  const lureVelAccumRef = useRef<number>(0); // smoothed horizontal velocity for direction
  const smoothDirectionRef = useRef<number>(1); // smoothly interpolated direction for turning
  const prevLureWyRef = useRef<number>(0); // previous frame lure Y for vertical velocity
  const smoothJigPitchRef = useRef<number>(0); // smoothed upward pitch during jig
  // Base coordinates
  const bobberX = 400;
  const bobberY = 400; // Seam between upper and lower housing - moved down from 300
  const waterSurfaceY = bobberY - 36; // Water sits 30% up the upper housing
  const bobberTopY = bobberY - 120; // Tip of the upper housing
  const bobberBottomY = bobberY + 75; // Pointed tip of the lower housing
  // Armature pivot point — mid-section of lower housing (where armature enters)
  const pivotX = bobberX;
  const pivotY = bobberY + 35;
  // Bottom tip of bobber — where fishing line exits
  const bobberTipY = bobberBottomY;
  // Armature dimensions (single straight bar hanging down)
  const armatureLengths: Record<string, number> = {
    XS: 40,
    S: 55,
    M: 70,
    L: 90,
    XL: 110,
    XXL: 130
  };
  const armatureLength = armatureLengths[state.armatureSize] ?? 70;
  // Line length scales with bobber stop depth — deeper = longer line from armature tip to lure
  const lineLength = state.bobberStopDepth * 20; // 1ft=20px, 6ft=120px, 20ft=400px
  const animate = (time: number) => {
    if (state.isRunning) {
      const dt = lastFrameRef.current > 0 ? Math.min((time - lastFrameRef.current) / 1000, 0.05) : 0.016;
      let activeMultiplier = 1;
      if (state.interval === 'Intermittent') {
        const cycleTime = timeRef.current % 5;
        if (cycleTime > 3) activeMultiplier = 0;
      }
      timeRef.current += dt * state.speed * 0.315 * activeMultiplier;
    }
    lastFrameRef.current = time;
    const t = timeRef.current;
    // Calculate pause countdown
    if (state.pauseAtTop && state.pauseDuration > 0 && onPauseCountdownChange) {
      const pattern = state.pattern;
      const baseSwingTime = pattern === 'Gentle Sway' ? 1.7 : 3.0;
      const angleScale = Math.max(1, state.swingAngle / 45);
      const scaledSwingTime = baseSwingTime * angleScale;
      const pausePoint = Math.min(state.pauseAngle, state.swingAngle) / state.swingAngle;
      const pause = state.pauseDuration;
      const dA = 1 + pausePoint;
      const dB = 1 - pausePoint;
      const dC = 1 + pausePoint;
      const dD = 1 - pausePoint;
      const totalDist = dA + dB + dC + dD;
      const tA = scaledSwingTime * (dA / totalDist);
      const tB = scaledSwingTime * (dB / totalDist);
      const tC = scaledSwingTime * (dC / totalDist);
      const cycleDuration = scaledSwingTime + 2 * pause;
      const phase = (t % cycleDuration + cycleDuration) % cycleDuration;
      const e1 = tA;
      const e2 = tA + pause;
      const e3 = tA + pause + tB;
      const e4 = tA + pause + tB + tC;
      const e5 = tA + pause + tB + tC + pause;
      let countdown = 0;
      if (phase >= e1 && phase < e2) {
        // In first pause
        countdown = e2 - phase;
      } else if (phase >= e4 && phase < e5) {
        // In second pause
        countdown = e5 - phase;
      }
      onPauseCountdownChange(Math.max(0, countdown));
    } else if (onPauseCountdownChange) {
      onPauseCountdownChange(0);
    }
    // Get normalized swing (-1 to 1) and multiply by configured angle
    const normalizedSwing = state.isRunning ? getArmatureSwing(state.pattern, t, state.pauseAtTop, state.pauseDuration, state.swingAngle, state.pauseAngle, state.sweepJerkiness) : 0;
    const swingAngle = normalizedSwing * state.swingAngle;
    // Jig displacement in pixels — applied directly to armature tip, independent of swing angle
    const jigDisplacement = state.isRunning ? getJigDisplacement(t, state.pattern, state.pauseAtTop, state.pauseDuration, state.jigAtPause, state.jigDistance, state.jigSpeedUp, state.jigPauseBetween, state.jigSpeedDown, state.swingAngle) : 0;
    // Convert jig displacement into angular offset — armature is a rigid bar that rotates
    // Jig pulls the armature TOWARD vertical (0°) — line pull brings tip straight down
    let jigAdjustedAngle = swingAngle;
    if (jigDisplacement > 0 && armatureLength > 0) {
      const jigAngleOffset = Math.atan2(jigDisplacement, armatureLength) * (180 / Math.PI);
      // Reduce the magnitude of swing angle (rotate toward vertical/straight down)
      // but never overshoot past 0° to the other side
      if (swingAngle > 0) {
        jigAdjustedAngle = Math.max(0, swingAngle - jigAngleOffset);
      } else if (swingAngle < 0) {
        jigAdjustedAngle = Math.min(0, swingAngle + jigAngleOffset);
      }
    }
    // Rotate the swing assembly — pivot stays fixed, armature is rigid
    if (swingGroupRef.current) {
      swingGroupRef.current.setAttribute('transform', `rotate(${jigAdjustedAngle}, ${pivotX}, ${pivotY})`);
    }
    // Armature tip at fixed length — rigid bar, no retraction
    {
      const tipLocalX = pivotX;
      const tipLocalY = pivotY + armatureLength;
      if (armatureLineRef.current) {
        armatureLineRef.current.setAttribute('x2', String(tipLocalX));
        armatureLineRef.current.setAttribute('y2', String(tipLocalY));
      }
      if (armatureTipDotRef.current) {
        armatureTipDotRef.current.setAttribute('cx', String(tipLocalX));
        armatureTipDotRef.current.setAttribute('cy', String(tipLocalY));
      }
    }
    // Update the bobber-tip-to-armature-tip line (outside swing group, needs world coords)
    if (bobberToArmLineRef.current) {
      const aRad = jigAdjustedAngle * Math.PI / 180;
      const tipWx = pivotX - armatureLength * Math.sin(aRad);
      const tipWy = pivotY + armatureLength * Math.cos(aRad);
      bobberToArmLineRef.current.setAttribute('x2', String(tipWx));
      bobberToArmLineRef.current.setAttribute('y2', String(tipWy));
    }
    // === LIFELIKE RUBBER FISH ANIMATION ===
    const lureTime = time * 0.001;
    // Horizontal lure velocity drives how active the tail looks
    const hVel = Math.abs(lureVxRef.current);
    const clampedHVel = Math.min(hVel, 3);
    const hFactor = clampedHVel / 3; // 0 to 1
    const isMoving = hVel > 0.08;
    // When not moving horizontally: barely any tail wag. Moving: lively wave.
    const tailSpeed = 0.08 + hFactor * 3.0; // 0.08 idle (very slow), up to ~3.1 when moving
    const tailAmp = 0.025 + hFactor * 0.75; // 0.025 idle (nearly still), up to ~0.775 when moving — 50% reduced
    // 1. TAIL & BODY animation — lure-type aware
    const turnFactor = 1 - Math.abs(smoothDirectionRef.current); // 0 = facing, 1 = mid-turn
    if (false) {
      // WORM: horizontal on hook shank, then droops down with lateral wave
      // Tail drag: when moving, the hanging tail straightens opposite to movement direction
      const vel = lureVxRef.current;
      // Drag offset increases with depth along the worm — deeper segments trail more
      // Negative vel = moving left, so tail drags right (positive offset), and vice versa
      const dragStrength = Math.min(1, Math.abs(vel) / 3); // 0-1 normalized speed
      const dragDir = vel > 0 ? -1 : vel < 0 ? 1 : 0; // opposite to movement
      // Each segment gets increasing drag offset + reduced Y droop (straightening)
      const d0 = dragDir * dragStrength * 1;
      const d1 = dragDir * dragStrength * 3;
      const d2 = dragDir * dragStrength * 6;
      const d3 = dragDir * dragStrength * 10;
      const d4 = dragDir * dragStrength * 14;
      // Vertical straightening — tail lifts up when moving fast
      const lift1 = dragStrength * 2;
      const lift2 = dragStrength * 5;
      const lift3 = dragStrength * 8;
      const lift4 = dragStrength * 12;
      if (lureTailRef.current) {
        const waveFreq = tailSpeed * 1.2;
        const waveAmp = 4 * tailAmp * (1 - dragStrength * 0.6); // dampen wave when moving fast
        const w0 = Math.sin(lureTime * waveFreq) * waveAmp * 0.05;
        const w1 = Math.sin(lureTime * waveFreq - 0.8) * waveAmp * 0.2;
        const w2 = Math.sin(lureTime * waveFreq - 1.6) * waveAmp * 0.5;
        const w3 = Math.sin(lureTime * waveFreq - 2.4) * waveAmp * 0.7;
        const w4 = Math.sin(lureTime * waveFreq - 3.2) * waveAmp;
        lureTailRef.current.setAttribute('d', `M 0 1 L -6 ${1 + w0 * 0.2} L -12 ${1 + w0 * 0.5} Q ${-18 + d0} ${1 + w1 * 0.3} ${-20 + w1 + d1} ${6 - lift1} Q ${-22 + w2 + d2} ${16 - lift2} ${-20 + w2 + d2} ${26 - lift2} Q ${-18 + w3 + d3} ${36 - lift3} ${-20 + w3 + d3} ${46 - lift3} Q ${-22 + w4 + d4} ${56 - lift4} ${-20 + w4 + d4} ${66 - lift4}`);
      }
      if (bodyPathRef.current) {
        const waveFreq = tailSpeed * 1.2;
        const waveAmp = 4 * tailAmp * (1 - dragStrength * 0.6);
        const w0 = Math.sin(lureTime * waveFreq) * waveAmp * 0.05;
        const w1 = Math.sin(lureTime * waveFreq - 0.8) * waveAmp * 0.2;
        const w2 = Math.sin(lureTime * waveFreq - 1.6) * waveAmp * 0.5;
        const w3 = Math.sin(lureTime * waveFreq - 2.4) * waveAmp * 0.7;
        const w4 = Math.sin(lureTime * waveFreq - 3.2) * waveAmp;
        bodyPathRef.current.setAttribute('d', `M 0 1 L -6 ${1 + w0 * 0.2} L -12 ${1 + w0 * 0.5} Q ${-18 + d0} ${1 + w1 * 0.3} ${-20 + w1 + d1} ${6 - lift1} Q ${-22 + w2 + d2} ${16 - lift2} ${-20 + w2 + d2} ${26 - lift2} Q ${-18 + w3 + d3} ${36 - lift3} ${-20 + w3 + d3} ${46 - lift3} Q ${-22 + w4 + d4} ${56 - lift4} ${-20 + w4 + d4} ${66 - lift4}`);
      }
    } else {
      // CRAWFISH: C-shape curl that flicks back to straight
      if (lureTailRef.current && state.lureType !== 'NedRig') {
        // SHAD / MINNOW / WHITEJIG: snappy tail wiggle (not a traveling wave)
        const wiggleFreq = tailSpeed * 2.8;
        const wiggleAmp = 15 * tailAmp; // increased 50% from 10
        const chaoticFreq = wiggleFreq * 2.3;
        const chaoticAmp = wiggleAmp * turnFactor * 1.0;
        const erratic = turnFactor * Math.sin(lureTime * 11.3) * 0.4;
        // Small phase offsets = tail moves as a unit (wiggle), not a traveling wave
        const baseWiggle = Math.sin(lureTime * wiggleFreq);
        const y0 = baseWiggle * wiggleAmp * 0.01;
        const y1 = Math.sin(lureTime * wiggleFreq - 0.1) * wiggleAmp * 0.04 + Math.sin(lureTime * chaoticFreq - 0.2) * chaoticAmp * 0.03;
        const y2 = Math.sin(lureTime * wiggleFreq - 0.2) * wiggleAmp * 0.12 + Math.sin(lureTime * chaoticFreq - 0.4) * chaoticAmp * 0.08;
        const y3 = Math.sin(lureTime * wiggleFreq - 0.3) * wiggleAmp * 0.35 + Math.sin(lureTime * chaoticFreq - 0.6 + erratic) * chaoticAmp * 0.2;
        const y4 = Math.sin(lureTime * wiggleFreq - 0.4) * wiggleAmp * 0.75 + Math.sin(lureTime * chaoticFreq - 0.8 + erratic) * chaoticAmp * 0.5;
        const y5 = Math.sin(lureTime * wiggleFreq - 0.5) * wiggleAmp * 1.4 + Math.sin(lureTime * chaoticFreq - 1.0 + erratic * 1.5) * chaoticAmp * 0.9;
        lureTailRef.current.setAttribute('d', `M 0 ${y0} C -3 ${y1} -6 ${y2} -9 ${y3} C -12 ${y3} -15 ${y4} -18 ${y5}`);
        if (tailForkRef.current) {
          const tipY = y5;
          tailForkRef.current.setAttribute('d', `M -18 ${tipY - 2} Q -20 ${tipY} -18 ${tipY + 2}`);
        }
      }
      // BODY FLEX: Subtle S-curve through the body
      if (bodyPathRef.current) {
        const bodyWave1 = Math.sin(lureTime * tailSpeed * 0.6) * 1.5 * tailAmp;
        const bodyWave2 = Math.sin(lureTime * tailSpeed * 0.6 - 0.5) * 2 * tailAmp;
        bodyPathRef.current.setAttribute('d', `M 0 0 C -5 ${bodyWave1} -14 ${bodyWave2} -28 ${bodyWave1 * 0.5} C -28 ${-bodyWave2} -14 ${-bodyWave1} 0 0 Z`);
      }
    }
    // 3. PECTORAL FINS: Rapid flutter when moving, gentle sway when still
    const finSpeed = isMoving ? 6 : 1.5;
    const finAmp = isMoving ? 3 : 1.5;
    if (pectoralTopRef.current) {
      const flutter = Math.sin(lureTime * finSpeed) * finAmp;
      pectoralTopRef.current.setAttribute('d', `M -8 -4 Q -6 ${-8 - flutter} -11 ${-7 - flutter * 0.5}`);
    }
    if (pectoralBotRef.current) {
      const flutter = Math.sin(lureTime * finSpeed + 0.5) * finAmp;
      pectoralBotRef.current.setAttribute('d', `M -8 4 Q -6 ${8 + flutter} -11 ${7 + flutter * 0.5}`);
    }
    // 4. DORSAL FIN: Ripple
    if (dorsalRef.current) {
      const ripple = Math.sin(lureTime * tailSpeed * 0.8) * 1.5 * tailAmp;
      dorsalRef.current.setAttribute('d', `M -18 -5.5 Q -14 ${-10 - ripple} -10 -5.5`);
    }
    // 5. PHYSICS-BASED LURE: gravity + line length constraint
    if (lureBodyRef.current) {
      const angleRad = jigAdjustedAngle * Math.PI / 180;
      // Armature tip in world space — jig is handled via rotation, not displacement
      const tipWx = pivotX - armatureLength * Math.sin(angleRad);
      const tipWy = pivotY + armatureLength * Math.cos(angleRad);
      // Initialize lure position, or reset when line length changes (depth adjusted)
      if (!lureInitializedRef.current || prevLineLengthRef.current !== lineLength) {
        lureWxRef.current = tipWx;
        lureWyRef.current = tipWy + lineLength;
        lureInitializedRef.current = true;
        prevLineLengthRef.current = lineLength;
      }
      // --- GRAVITY: reduced when lure is swung out on the arc ---
      // Full gravity when lure hangs straight down; gentle float when displaced to the side.
      // This prevents the fast pendulum snap-back — lure drifts down slowly through water.
      const baseGravity = 1.2;
      const dxFromTip = lureWxRef.current - tipWx;
      const dyFromTip = lureWyRef.current - tipWy;
      const distFromTip = Math.sqrt(dxFromTip * dxFromTip + dyFromTip * dyFromTip);
      // How far the lure is displaced sideways (0 = directly below, 1 = fully horizontal)
      const lateralRatio = distFromTip > 1 ? Math.abs(dxFromTip) / distFromTip : 0;
      // During active jigging, the tip moves DOWN (toward vertical), which would
      // drag the lure down via the constraint. Reduce gravity so the lure drifts
      // gently instead of being pulled.
      const jigActive = jigDisplacement > 0.5;
      const jigGravityScale = jigActive ? 0.15 : 1;
      // Scale gravity: full when hanging straight, reduced when swung out (but not too much)
      const gravity = baseGravity * (1 - lateralRatio * 0.45) * jigGravityScale;
      lureWyRef.current += gravity;
      // --- WATER DRAG: friction opposing velocity ---
      const waterDragX = 0.992; // horizontal: enough momentum for swing, enough drag to slow return
      lureVxRef.current *= waterDragX;
      // Apply velocity to position
      lureWxRef.current += lureVxRef.current;
      // --- LINE LENGTH CONSTRAINT ---
      let isSlack = false;
      let dist = 0;
      const prevLureX = lureWxRef.current;
      // More iterations at large angles or during jigging for stability
      const constraintIters = state.swingAngle > 60 || state.jigAtPause ? 4 : 2;
      const tipDxFrame = tipWx - prevTipWxRef.current;
      for (let i = 0; i < constraintIters; i++) {
        const dx = lureWxRef.current - tipWx;
        const dy = lureWyRef.current - tipWy;
        dist = Math.sqrt(dx * dx + dy * dy);
        if (dist > lineLength) {
          const nx = dx / dist;
          const ny = dy / dist;
          const preSnapX = lureWxRef.current;
          lureWxRef.current = tipWx + nx * lineLength;
          lureWyRef.current = tipWy + ny * lineLength;
          // --- ANTI-PENDULUM: undo horizontal snap that swings lure back toward vertical ---
          // Gravity pulls lure down → constraint snaps to arc → snap moves lure toward vertical.
          // In water, the lure should stay wide and sink, not pendulum swing back.
          // Only allow horizontal constraint motion from genuine tip pulls.
          const snapDx = lureWxRef.current - preSnapX;
          const lureOffsetX = preSnapX - tipWx;
          const snapTowardVertical = lureOffsetX > 2 && snapDx < -0.1 || lureOffsetX < -2 && snapDx > 0.1;
          const tipDyFrame = tipWy - prevTipWyRef.current;
          const tipIsActivelyMoving = Math.abs(tipDxFrame) > 0.5 || Math.abs(tipDyFrame) > 0.5;
          if (snapTowardVertical && !tipIsActivelyMoving) {
            // Preserve the lure's horizontal position — don't swing it back toward vertical.
            lureWxRef.current = preSnapX;
            // Recalculate Y to maintain line length (only allow moving DOWN, not up)
            const dx2 = lureWxRef.current - tipWx;
            const absX = Math.abs(dx2);
            if (absX < lineLength) {
              const newY = tipWy + Math.sqrt(lineLength * lineLength - dx2 * dx2);
              // Only move lure down, never snap it upward
              lureWyRef.current = Math.max(lureWyRef.current, newY);
            }
          }
          isSlack = false;
        } else {
          isSlack = true;
        }
      }
      if (!isSlack) {
        const pullDx = lureWxRef.current - prevLureX;
        const tipDx = tipWx - prevTipWxRef.current;
        // Genuine pull: constraint moved lure in the same direction the tip moved
        const isGenuinePull = pullDx > 0 && tipDx > 0 || pullDx < 0 && tipDx < 0;
        if (isGenuinePull) {
          const isPullBack = pullDx > 0 && lureVxRef.current < -0.2 || pullDx < 0 && lureVxRef.current > 0.2;
          // Scale down transfer as lure velocity builds — water resistance increases with speed
          const currentSpeed = Math.abs(lureVxRef.current);
          const speedFactor = Math.max(0.1, 1 - currentSpeed / 8);
          const basePull = isPullBack ? 0.12 : 0.4;
          lureVxRef.current += pullDx * basePull * speedFactor;
        }
        // Cap maximum lure velocity — water prevents unlimited acceleration
        const maxVx = 3.5;
        if (lureVxRef.current > maxVx) lureVxRef.current = maxVx;
        if (lureVxRef.current < -maxVx) lureVxRef.current = -maxVx;
      }
      // Only enforce exact line length when line is TAUT (not slack)
      // This allows natural slack to form when armature drops faster than lure sinks
      if (!isSlack) {
        const dx = lureWxRef.current - tipWx;
        const dy = lureWyRef.current - tipWy;
        dist = Math.sqrt(dx * dx + dy * dy);
        if (dist > lineLength * 0.98) {
          const nx = dx / dist;
          const ny = dy / dist;
          lureWxRef.current = tipWx + nx * lineLength;
          lureWyRef.current = tipWy + ny * lineLength;
          dist = lineLength;
        }
      }
      // --- DIRECTION TRACKING: based on LURE's own movement, not armature ---
      // The lure only changes direction when the line has pulled it and it's
      // actually moving in the new direction. This creates a natural delay:
      // armature reverses → line goes slack → line catches → pulls lure → lure turns.
      prevTipWxRef.current = tipWx;
      prevTipWyRef.current = tipWy;
      const lureDx = lureWxRef.current - prevLureWxRef.current;
      prevLureWxRef.current = lureWxRef.current;
      // Only update direction when line is taut AND lure is actually moving
      if (!isSlack && Math.abs(lureDx) > 0.15) {
        // Accumulate lure's own movement direction
        lureVelAccumRef.current = lureVelAccumRef.current * 0.88 + lureDx * 0.12;
        if (Math.abs(lureVelAccumRef.current) > 0.12) {
          // Lure faces the direction it's actually being pulled
          lureDirectionRef.current = lureVelAccumRef.current > 0 ? 1 : -1;
        }
      } else if (isSlack) {
        // While slack, decay the accumulator so stale direction doesn't persist
        lureVelAccumRef.current *= 0.92;
      }
      prevSwingAngleRef.current = swingAngle;
      const targetDirection = lureDirectionRef.current;
      // Smoothly interpolate toward target direction — creates a turning-in-water effect
      // The fish narrows (scale passes through ~0) then expands facing the other way
      const turnSpeed = 0.04; // lower = slower, more graceful turn
      smoothDirectionRef.current += (targetDirection - smoothDirectionRef.current) * turnSpeed;
      const smoothDir = smoothDirectionRef.current;
      // Add a slight pitch rotation during the turn (when scale is near 0, fish is sideways)
      const turnAmount = 1 - Math.abs(smoothDir); // 0 when facing fully, 1 at mid-turn
      const turnPitch = turnAmount * 12; // slight upward tilt during turn
      // --- VERTICAL PITCH: lure angles nose-up when rising, nose-down when sinking ---
      const lureVelY = lureWyRef.current - prevLureWyRef.current;
      prevLureWyRef.current = lureWyRef.current;
      let targetJigPitch = 0;
      if (lureVelY < -0.1) {
        // Lure is moving upward — tilt nose up proportionally
        const upSpeed = Math.min(Math.abs(lureVelY), 5);
        targetJigPitch = upSpeed * 9; // up to ~45° nose-up tilt
      } else if (lureVelY > 0.15) {
        // Lure is actively sinking — tilt nose down proportionally (not triggered by idle jitter)
        const downSpeed = Math.min(Math.abs(lureVelY), 5);
        targetJigPitch = -downSpeed * 7; // up to ~35° nose-down tilt, no minimum bias
      }
      // --- LINE-PULL PITCH: when line is taut and pulling laterally, angle lure nose-up ---
      if (!isSlack) {
        const ldx = tipWx - lureWxRef.current;
        const ldy = tipWy - lureWyRef.current;
        const lineDist = Math.sqrt(ldx * ldx + ldy * ldy);
        if (lineDist > 1) {
          // Angle of line from vertical: 0° = straight down, 90° = horizontal
          const lineAngleFromVert = Math.atan2(Math.abs(ldx), Math.abs(ldy)) * (180 / Math.PI);
          // Only apply when line is pulling upward (tip above lure) AND has lateral component
          // When line is straight down (lineAngleFromVert ≈ 0), no tilt — lure hangs horizontal
          // When line pulls at an angle, tilt proportional to lateral tension
          if (ldy < 0) {
            const lateralStrength = Math.min(1, lineAngleFromVert / 45); // 0 at vertical, 1 at 45°+
            targetJigPitch += lateralStrength * 25; // up to 25° nose-up from lateral line pull
          }
        }
      }
      // Smooth the pitch so it eases in and out naturally
      smoothJigPitchRef.current += (targetJigPitch - smoothJigPitchRef.current) * 0.1;
      const jigPitch = smoothJigPitchRef.current;
      // --- CONVERT world position to local (swing group) coordinates ---
      const worldLureX = lureWxRef.current;
      const worldLureY = lureWyRef.current;
      const dwx = worldLureX - pivotX;
      const dwy = worldLureY - pivotY;
      const cosA = Math.cos(angleRad);
      const sinA = Math.sin(angleRad);
      const localX = pivotX + dwx * cosA + dwy * sinA;
      const localY = pivotY - dwx * sinA + dwy * cosA;
      const fishX = localX;
      const fishY = localY;
      // Counter-rotate to keep fish horizontal, with turn pitch + jig pitch
      const totalPitch = turnPitch - jigPitch * smoothDir; // negate for SVG clockwise convention; smoothDir handles mirror flip
      lureBodyRef.current.setAttribute('transform', `translate(${fishX}, ${fishY}) rotate(${-swingAngle + totalPitch}) scale(${smoothDir * lureScale}, ${lureScale})`);
      // --- FISHING LINE: curved path showing slack/taut ---
      if (fishingLineRef.current) {
        // Armature tip in local space — retracted during jig
        const startX = pivotX;
        const startY = pivotY + armatureLength;
        // Attach line to the hook eye (local 0, -7) transformed through lure rotation
        const eyeLocalX = 0;
        const eyeLocalY = -7;
        const lureRotRad = (-swingAngle + totalPitch) * Math.PI / 180;
        const endX = fishX + eyeLocalX * Math.cos(lureRotRad) - eyeLocalY * Math.sin(lureRotRad);
        const endY = fishY + eyeLocalX * Math.sin(lureRotRad) + eyeLocalY * Math.cos(lureRotRad);
        const midX = (startX + endX) / 2;
        const midY = (startY + endY) / 2;
        // Sag direction: world-down in local space = (sin(a), cos(a))
        const sagDirX = Math.sin(angleRad);
        const sagDirY = Math.cos(angleRad);
        // Slack amount: how much shorter the straight-line distance is than lineLength
        const slackAmount = isSlack ? lineLength - dist : 0;
        // Base sag: nearly zero when taut, pronounced droop when slack
        const baseSag = isSlack ? 12 : 1;
        const sagAmount = baseSag + slackAmount * 0.6;
        const cpX = midX + sagDirX * sagAmount;
        const cpY = midY + sagDirY * sagAmount;
        fishingLineRef.current.setAttribute('d', `M ${startX} ${startY} Q ${cpX} ${cpY} ${endX} ${endY}`);
      }
    }
    // Animate Waves
    if (wave1Ref.current && wave2Ref.current) {
      const waveT = time * 0.001;
      const wy = waterSurfaceY;
      const w1 = `M 0 ${wy} Q 200 ${wy + Math.sin(waveT) * 10} 400 ${wy} T 800 ${wy} L 800 800 L 0 800 Z`;
      const w2 = `M 0 ${wy + 10} Q 200 ${wy + 10 + Math.cos(waveT * 1.2) * 15} 400 ${wy + 10} T 800 ${wy + 10} L 800 800 L 0 800 Z`;
      wave1Ref.current.setAttribute('d', w1);
      wave2Ref.current.setAttribute('d', w2);
    }
    requestRef.current = requestAnimationFrame(animate);
  };
  useEffect(() => {
    requestRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(requestRef.current!);
  }, [state]);
  // Pre-calculate armature tip position (at rest, hanging straight down)
  const armatureTipY = pivotY + armatureLength;
  const baitY = armatureTipY + lineLength;
  return <div className="w-full h-full relative overflow-hidden" style={{
    background: 'linear-gradient(to bottom, #87CEEB 0%, #B0D4E8 30%, #E8C99B 75%, #F4A460 100%)'
  }} data-id="element-410">
      <LureSelector selectedLureColor={state.lureColor} onLureColorChange={onLureColorChange} selectedLureSize={state.lureSize} onLureSizeChange={onLureSizeChange} selectedArmatureSize={state.armatureSize} onArmatureSizeChange={onArmatureSizeChange} bobberStopDepth={state.bobberStopDepth} onBobberStopDepthChange={onBobberStopDepthChange} position={lureSelectorPosition} presets={presets} activePreset={activePreset} onPresetPlay={onPresetPlay} onPresetSave={onPresetSave} data-id="element-411" />

      {/* Zoomable content wrapper */}
      <div className="absolute inset-0" data-id="element-412">
        {/* Background glow behind bobber */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-[#00d4ff]/5 rounded-full blur-3xl pointer-events-none" data-id="element-413" />

        <svg width="100%" height="100%" viewBox={zoomLevel >= 1 ? `${(800 - 800 / zoomLevel) / 2} ${waterSurfaceY - 800 / zoomLevel * 0.45} ${800 / zoomLevel} ${800 / zoomLevel}` : `0 ${waterSurfaceY - 800 / zoomLevel * 0.15} 800 ${800 / zoomLevel}`} preserveAspectRatio="xMidYMid slice" className="absolute inset-0" style={{
        transition: 'all 0.2s ease-out'
      }} data-id="element-414">
          {/* Gradients */}
          <defs data-id="element-415">
            {/* Sky gradient */}
            <linearGradient id="skyGradient" x1="0%" y1="0%" x2="0%" y2="100%" data-id="element-416">
              <stop offset="0%" stopColor="#FF6B35" data-id="element-417" />
              <stop offset="30%" stopColor="#FF8C42" data-id="element-418" />
              <stop offset="60%" stopColor="#FFA552" data-id="element-419" />
              <stop offset="80%" stopColor="#FFB86B" data-id="element-420" />
              <stop offset="92%" stopColor="#FFC785" data-id="element-421" />
              <stop offset="100%" stopColor="#FFD4A3" data-id="element-422" />
            </linearGradient>
            {/* Sun glow */}
            <radialGradient id="sunGlow" cx="65%" cy="85%" r="35%" data-id="element-423">
              <stop offset="0%" stopColor="#FFF5E0" stopOpacity="0.9" data-id="element-424" />
              <stop offset="25%" stopColor="#FFD699" stopOpacity="0.5" data-id="element-425" />
              <stop offset="60%" stopColor="#E8A96A" stopOpacity="0.2" data-id="element-426" />
              <stop offset="100%" stopColor="#E8A96A" stopOpacity="0" data-id="element-427" />
            </radialGradient>
            {/* Horizon haze */}
            <linearGradient id="horizonHaze" x1="0%" y1="0%" x2="0%" y2="100%" data-id="element-428">
              <stop offset="0%" stopColor="#ffffff" stopOpacity="0" data-id="element-429" />
              <stop offset="70%" stopColor="#ffffff" stopOpacity="0.05" data-id="element-430" />
              <stop offset="100%" stopColor="#FFF5E0" stopOpacity="0.25" data-id="element-431" />
            </linearGradient>
            <radialGradient id="jigHeadShading" cx="40%" cy="35%" r="60%" data-id="element-432">
              <stop offset="0%" stopColor="#c0c0c0" stopOpacity="0.5" data-id="element-433" />
              <stop offset="60%" stopColor="#6a6a6a" stopOpacity="0.3" data-id="element-434" />
              <stop offset="100%" stopColor="#3a3a3a" stopOpacity="0.6" data-id="element-435" />
            </radialGradient>
            <linearGradient id="rayGradient" x1="0%" y1="0%" x2="0%" y2="100%" data-id="element-436">
              <stop offset="0%" stopColor="#00d4ff" stopOpacity="0.15" data-id="element-437" />
              <stop offset="100%" stopColor="#00d4ff" stopOpacity="0" data-id="element-438" />
            </linearGradient>
            <linearGradient id={`copperGradient-${instanceId}`} x1="0%" y1="0%" x2="100%" y2="0%" data-id="element-439">
              <stop offset="0%" stopColor="#c96b3c" data-id="element-440" />
              <stop offset="20%" stopColor="#e88a55" data-id="element-441" />
              <stop offset="50%" stopColor="#b85c38" data-id="element-442" />
              <stop offset="80%" stopColor="#8b4513" data-id="element-443" />
              <stop offset="100%" stopColor="#5a2a0a" data-id="element-444" />
            </linearGradient>
            <linearGradient id={`specularHighlight-${instanceId}`} x1="0%" y1="0%" x2="100%" y2="0%" data-id="element-445">
              <stop offset="0%" stopColor="#ffffff" stopOpacity="0" data-id="element-446" />
              <stop offset="15%" stopColor="#ffffff" stopOpacity="0.6" data-id="element-447" />
              <stop offset="25%" stopColor="#ffffff" stopOpacity="0" data-id="element-448" />
              <stop offset="100%" stopColor="#ffffff" stopOpacity="0" data-id="element-449" />
            </linearGradient>
            <linearGradient id={`translucentGray-${instanceId}`} x1="0%" y1="0%" x2="100%" y2="0%" data-id="element-450">
              <stop offset="0%" stopColor="#c8dce8" stopOpacity="0.25" data-id="element-451" />
              <stop offset="30%" stopColor="#e8f4ff" stopOpacity="0.12" data-id="element-452" />
              <stop offset="50%" stopColor="#ffffff" stopOpacity="0.08" data-id="element-453" />
              <stop offset="70%" stopColor="#e8f4ff" stopOpacity="0.12" data-id="element-454" />
              <stop offset="100%" stopColor="#a0b8c8" stopOpacity="0.3" data-id="element-455" />
            </linearGradient>
          </defs>

          {/* ===== SKY ===== */}
          {/* Sky background */}
          <rect x="0" y="0" width="800" height={waterSurfaceY + 10} fill="url(#skyGradient)" data-id="element-456" />

          {/* Sun glow near horizon */}
          <rect x="0" y="0" width="800" height={waterSurfaceY + 10} fill="url(#sunGlow)" data-id="element-457" />

          {/* Horizon haze */}
          <rect x="0" y="0" width="800" height={waterSurfaceY + 10} fill="url(#horizonHaze)" data-id="element-458" />

          {/* Sun disc — partially behind horizon */}
          <circle cx="520" cy={waterSurfaceY - 8} r="30" fill="#FFF0C8" opacity="0.7" data-id="element-459" />
          <circle cx="520" cy={waterSurfaceY - 8} r="22" fill="#FFFAE6" opacity="0.85" data-id="element-460" />
          <circle cx="520" cy={waterSurfaceY - 8} r="50" fill="#FFD699" opacity="0.12" data-id="element-461" />

          {/* Wispy clouds */}
          {/* Cloud 1 — large, left side */}
          <g opacity="0.5" data-id="element-462">
            <ellipse cx="180" cy="80" rx="80" ry="18" fill="white" opacity="0.6" data-id="element-463" />
            <ellipse cx="210" cy="74" rx="55" ry="14" fill="white" opacity="0.5" data-id="element-464" />
            <ellipse cx="150" cy="85" rx="40" ry="10" fill="white" opacity="0.4" data-id="element-465" />
          </g>
          {/* Cloud 2 — small, upper right */}
          <g opacity="0.4" data-id="element-466">
            <ellipse cx="600" cy="55" rx="50" ry="12" fill="white" opacity="0.5" data-id="element-467" />
            <ellipse cx="625" cy="50" rx="35" ry="9" fill="white" opacity="0.4" data-id="element-468" />
          </g>
          {/* Cloud 3 — wispy, mid-left */}
          <g opacity="0.35" data-id="element-469">
            <ellipse cx="100" cy="140" rx="60" ry="8" fill="white" opacity="0.4" data-id="element-470" />
            <ellipse cx="130" cy="137" rx="40" ry="6" fill="white" opacity="0.3" data-id="element-471" />
          </g>
          {/* Cloud 4 — near horizon, warm tinted */}
          <g opacity="0.4" data-id="element-472">
            <ellipse cx="350" cy={waterSurfaceY - 60} rx="90" ry="12" fill="#FFF5E0" opacity="0.4" data-id="element-473" />
            <ellipse cx="380" cy={waterSurfaceY - 65} rx="60" ry="9" fill="#FFF5E0" opacity="0.3" data-id="element-474" />
          </g>
          {/* Cloud 5 — right side near horizon */}
          <g opacity="0.35" data-id="element-475">
            <ellipse cx="680" cy={waterSurfaceY - 45} rx="70" ry="10" fill="#FFE8C8" opacity="0.35" data-id="element-476" />
            <ellipse cx="700" cy={waterSurfaceY - 50} rx="45" ry="7" fill="#FFE8C8" opacity="0.25" data-id="element-477" />
          </g>

          {/* Sun reflection on water surface */}
          <ellipse cx="520" cy={waterSurfaceY + 5} rx="60" ry="4" fill="#FFD699" opacity="0.25" data-id="element-478" />
          <ellipse cx="520" cy={waterSurfaceY + 12} rx="40" ry="3" fill="#FFD699" opacity="0.15" data-id="element-479" />

          {/* ===== FISHING LINE — ABOVE BOBBER (static, goes off screen) ===== */}
          <line x1={bobberX} y1={0} x2={bobberX} y2={bobberTopY} stroke="rgba(0, 0, 0, 0.15)" strokeWidth="1" data-id="element-480" />

          {/* ===== SUBTLE DEPTH SCALE — in the water ===== */}
          {(() => {
          const depthStartY = waterSurfaceY;
          const maxDepthFt = 20;
          const pixelsPerFoot = 25;
          const marks = [];
          for (let ft = 1; ft <= maxDepthFt; ft++) {
            const y = depthStartY + ft * pixelsPerFoot;
            const isMajor = ft % 5 === 0;
            // Fade out with depth
            const depthFade = Math.max(0.03, 0.12 - ft * 0.004);
            marks.push(<g key={ft} data-id="element-481">
                  {/* Subtle horizontal line across the water */}
                  <line x1={150} y1={y} x2={isMajor ? 650 : 200} y2={y} stroke="rgba(255,255,255,1)" strokeWidth={isMajor ? 0.5 : 0.3} opacity={isMajor ? 0.15 : 0.06} data-id="element-482" />
                  {/* Depth label — only on major marks */}
                  {isMajor && <text x={155} y={y - 4} fill="rgba(255,255,255,1)" fontSize="9" fontFamily="monospace" opacity={0.25} data-id="element-483">
                      {ft}ft
                    </text>}
                </g>);
          }
          return <g data-id="element-484">{marks}</g>;
        })()}

          {/* Water body — colored by water selection */}
          <rect x="0" y={waterSurfaceY} width="800" height={800 - waterSurfaceY} fill="#122a4a" opacity="0.9" data-id="element-485" />
          {/* Water Surface / Background */}
          <path ref={wave1Ref} fill="#1a4a7a" opacity="0.8" data-id="element-486" />
          <path ref={wave2Ref} fill="#2563a0" opacity="0.6" data-id="element-487" />

          {/* Underwater Light Rays */}
          <polygon points={`350,${waterSurfaceY} 450,${waterSurfaceY} 600,800 200,800`} fill="url(#rayGradient)" opacity="0.3" data-id="element-488" />

          {/* ===== FISHING LINE — THROUGH BOBBER (static, behind bobber body) ===== */}
          <line x1={bobberX} y1={bobberTopY} x2={bobberX} y2={bobberTipY} stroke="rgba(255, 255, 255, 0.15)" strokeWidth="1" data-id="element-489" />

          {/* --- BOBBER BODY (static, doesn't swing) --- */}
          <g id="bobber-body" data-id="element-490">
            {/* Upper Housing (Copper Capsule with tapered tip) */}
            <g data-id="element-491">
              <path d={`
                M ${bobberX - 22} ${bobberY} 
                L ${bobberX - 22} ${bobberY - 45} 
                C ${bobberX - 22} ${bobberY - 70}, ${bobberX - 6} ${bobberY - 75}, ${bobberX - 4} ${bobberY - 110} 
                C ${bobberX - 4} ${bobberY - 120}, ${bobberX + 4} ${bobberY - 120}, ${bobberX + 4} ${bobberY - 110} 
                C ${bobberX + 6} ${bobberY - 75}, ${bobberX + 22} ${bobberY - 70}, ${bobberX + 22} ${bobberY - 45} 
                L ${bobberX + 22} ${bobberY} 
                Z
              `} fill={`url(#copperGradient-${instanceId})`} data-id="element-492" />
              <path d={`
                M ${bobberX - 22} ${bobberY} 
                L ${bobberX - 22} ${bobberY - 45} 
                C ${bobberX - 22} ${bobberY - 70}, ${bobberX - 6} ${bobberY - 75}, ${bobberX - 4} ${bobberY - 110} 
                C ${bobberX - 4} ${bobberY - 120}, ${bobberX + 4} ${bobberY - 120}, ${bobberX + 4} ${bobberY - 110} 
                C ${bobberX + 6} ${bobberY - 75}, ${bobberX + 22} ${bobberY - 70}, ${bobberX + 22} ${bobberY - 45} 
                L ${bobberX + 22} ${bobberY} 
                Z
              `} fill={`url(#specularHighlight-${instanceId})`} data-id="element-493" />
            </g>

            {/* Seam / Coupling Ring */}
            <rect x={bobberX - 23} y={bobberY - 4} width="46" height="8" fill="#e0e0e0" rx="2" data-id="element-494" />
            <rect x={bobberX - 23} y={bobberY - 4} width="46" height="8" fill={`url(#specularHighlight-${instanceId})`} rx="2" opacity="0.8" data-id="element-495" />

            {/* Lower Housing (Pointed Tapered Shape) */}
            <g data-id="element-496">
              <path d={`
                M ${bobberX - 21} ${bobberY + 4} 
                L ${bobberX - 21} ${bobberY + 35} 
                C ${bobberX - 21} ${bobberY + 55}, ${bobberX - 8} ${bobberY + 68}, ${bobberX} ${bobberTipY} 
                C ${bobberX + 8} ${bobberY + 68}, ${bobberX + 21} ${bobberY + 55}, ${bobberX + 21} ${bobberY + 35} 
                L ${bobberX + 21} ${bobberY + 4} 
                Z
              `} fill={`url(#translucentGray-${instanceId})`} stroke="rgba(180,210,230,0.4)" strokeWidth="0.8" data-id="element-497" />
              <path d={`
                M ${bobberX - 21} ${bobberY + 4} 
                L ${bobberX - 21} ${bobberY + 35} 
                C ${bobberX - 21} ${bobberY + 55}, ${bobberX - 8} ${bobberY + 68}, ${bobberX} ${bobberTipY} 
                C ${bobberX + 8} ${bobberY + 68}, ${bobberX + 21} ${bobberY + 55}, ${bobberX + 21} ${bobberY + 35} 
                L ${bobberX + 21} ${bobberY + 4} 
                Z
              `} fill={`url(#specularHighlight-${instanceId})`} opacity="0.5" data-id="element-498" />
              {/* ===== VISIBLE INTERNALS through clear housing ===== */}

              {/* Circuit Board (PCB) — green with traces */}
              <rect x={bobberX - 14} y={bobberY + 8} width="28" height="14" fill="#1a6b2a" rx="1.5" opacity="0.9" data-id="element-499" />
              {/* PCB copper traces */}
              <line x1={bobberX - 10} y1={bobberY + 11} x2={bobberX + 4} y2={bobberY + 11} stroke="#c8a832" strokeWidth="0.7" opacity="0.8" data-id="element-500" />
              <line x1={bobberX - 6} y1={bobberY + 14} x2={bobberX + 10} y2={bobberY + 14} stroke="#c8a832" strokeWidth="0.5" opacity="0.7" data-id="element-501" />
              <line x1={bobberX - 8} y1={bobberY + 17} x2={bobberX + 6} y2={bobberY + 17} stroke="#c8a832" strokeWidth="0.6" opacity="0.7" data-id="element-502" />
              {/* PCB solder points */}
              <circle cx={bobberX - 8} cy={bobberY + 11} r="1" fill="#d4d4d4" opacity="0.8" data-id="element-503" />
              <circle cx={bobberX + 6} cy={bobberY + 14} r="1" fill="#d4d4d4" opacity="0.8" data-id="element-504" />
              <circle cx={bobberX - 3} cy={bobberY + 17} r="1" fill="#d4d4d4" opacity="0.7" data-id="element-505" />
              <circle cx={bobberX + 9} cy={bobberY + 11} r="0.8" fill="#d4d4d4" opacity="0.7" data-id="element-506" />
              {/* Small IC chip on PCB */}
              <rect x={bobberX - 4} y={bobberY + 12} width="6" height="4" fill="#1a1a1a" rx="0.5" opacity="0.85" data-id="element-507" />
              <rect x={bobberX - 4.5} y={bobberY + 13} width="0.8" height="0.8" fill="#c8a832" opacity="0.7" data-id="element-508" />
              <rect x={bobberX - 4.5} y={bobberY + 14.5} width="0.8" height="0.8" fill="#c8a832" opacity="0.7" data-id="element-509" />
              <rect x={bobberX + 1.5} y={bobberY + 13} width="0.8" height="0.8" fill="#c8a832" opacity="0.7" data-id="element-510" />
              <rect x={bobberX + 1.5} y={bobberY + 14.5} width="0.8" height="0.8" fill="#c8a832" opacity="0.7" data-id="element-511" />

              {/* Motor — larger DC motor touching PCB */}
              <rect x={bobberX - 10} y={bobberY + 22} width="20" height="18" fill="#707070" rx="3" opacity="0.9" data-id="element-512" />
              {/* Motor casing highlight */}
              <rect x={bobberX - 8} y={bobberY + 23.5} width="16" height="3" fill="#909090" opacity="0.6" rx="1" data-id="element-513" />
              {/* Motor shaft */}
              <rect x={bobberX - 1.5} y={bobberY + 40} width="3" height="6" fill="#a0a0a0" opacity="0.85" data-id="element-514" />
              {/* Motor label */}
              <rect x={bobberX - 5} y={bobberY + 28} width="10" height="6" fill="#505050" rx="0.5" opacity="0.7" data-id="element-515" />
              <line x1={bobberX - 3} y1={bobberY + 30.5} x2={bobberX + 3} y2={bobberY + 30.5} stroke="#c8a832" strokeWidth="0.5" opacity="0.6" data-id="element-516" />

              {/* Wires — from battery terminals UP to motor and PCB */}
              {/* Red wire: battery + terminal up to motor */}
              <path d={`M ${bobberX + 1.5} ${bobberY + 53} Q ${bobberX + 6} ${bobberY + 48} ${bobberX + 5} ${bobberY + 38}`} stroke="#cc2222" strokeWidth="0.7" fill="none" opacity="0.75" data-id="element-517" />
              {/* Red wire continues: motor up to PCB */}
              <path d={`M ${bobberX + 5} ${bobberY + 26} Q ${bobberX + 8} ${bobberY + 24} ${bobberX + 6} ${bobberY + 22}`} stroke="#cc2222" strokeWidth="0.6" fill="none" opacity="0.7" data-id="element-518" />
              {/* Black wire: battery - terminal up to motor */}
              <path d={`M ${bobberX - 1.5} ${bobberY + 53} Q ${bobberX - 6} ${bobberY + 48} ${bobberX - 5} ${bobberY + 38}`} stroke="#1a1a1a" strokeWidth="0.7" fill="none" opacity="0.75" data-id="element-519" />
              {/* Black wire continues: motor up to PCB */}
              <path d={`M ${bobberX - 5} ${bobberY + 26} Q ${bobberX - 8} ${bobberY + 24} ${bobberX - 6} ${bobberY + 22}`} stroke="#1a1a1a" strokeWidth="0.6" fill="none" opacity="0.7" data-id="element-520" />
            </g>

            {/* Coupling Point — where armature enters bobber */}
            <circle cx={pivotX} cy={pivotY} r="4" fill="#000000" data-id="element-521" />
            <circle cx={pivotX} cy={pivotY} r="2" fill="#00d4ff" data-id="element-522" />

            {/* Bottom opening — rounded hole where line exits */}
            <ellipse cx={bobberX} cy={bobberTipY} rx="3" ry="2" fill="#0a1628" data-id="element-523" />
            <ellipse cx={bobberX} cy={bobberTipY} rx="3" ry="2" fill="none" stroke="#6a7a8a" strokeWidth="1" data-id="element-524" />
          </g>

          {/* ===== RED BEAD — on top of bobber, below the knot ===== */}
          <g data-id="element-525">
            <ellipse cx={bobberX} cy={bobberTopY + 6} rx="6" ry="4.5" fill="#cc2222" data-id="element-526" />
            {/* Highlight for 3D look */}
            <ellipse cx={bobberX - 1.2} cy={bobberTopY + 4.5} rx="2.5" ry="1.8" fill="#ff5555" opacity="0.6" data-id="element-527" />
            {/* Subtle shadow underneath */}
            <ellipse cx={bobberX} cy={bobberTopY + 9} rx="4" ry="1.2" fill="#000000" opacity="0.15" data-id="element-528" />
          </g>

          {/* ===== BOBBER STOP — knot on top of bead ===== */}
          <g data-id="element-529">
            {/* Tight wrap 1 */}
            <path d={`M ${bobberX - 2.5} ${bobberTopY - 3} Q ${bobberX} ${bobberTopY - 5} ${bobberX + 2.5} ${bobberTopY - 3}`} fill="none" stroke="#39ff14" strokeWidth="1.6" strokeLinecap="round" data-id="element-530" />
            {/* Tight wrap 2 */}
            <path d={`M ${bobberX + 2.5} ${bobberTopY - 1.5} Q ${bobberX} ${bobberTopY - 4} ${bobberX - 2.5} ${bobberTopY - 1.5}`} fill="none" stroke="#39ff14" strokeWidth="1.6" strokeLinecap="round" data-id="element-531" />
            {/* Tight wrap 3 — center knot bulge */}
            <path d={`M ${bobberX - 2} ${bobberTopY - 0.5} Q ${bobberX} ${bobberTopY - 3} ${bobberX + 2} ${bobberTopY - 0.5}`} fill="none" stroke="#39ff14" strokeWidth="1.8" strokeLinecap="round" data-id="element-532" />
            {/* Tag end 1 — points up */}
            <path d={`M ${bobberX - 2} ${bobberTopY - 3} Q ${bobberX - 6} ${bobberTopY - 8} ${bobberX - 9} ${bobberTopY - 12}`} fill="none" stroke="#39ff14" strokeWidth="1" strokeLinecap="round" data-id="element-533" />
            {/* Tag end 2 */}
            <path d={`M ${bobberX + 2} ${bobberTopY - 1.5} Q ${bobberX + 7} ${bobberTopY - 1} ${bobberX + 11} ${bobberTopY + 4}`} fill="none" stroke="#39ff14" strokeWidth="1" strokeLinecap="round" data-id="element-534" />
            {/* Subtle glow */}
            <circle cx={bobberX} cy={bobberTopY - 1} r="4" fill="#39ff14" opacity="0.06" data-id="element-535" />
          </g>

          {/* ===== FISHING LINE — from bobber bottom tip to armature tip (dynamic) ===== */}
          <line ref={bobberToArmLineRef} x1={bobberX} y1={bobberTipY} x2={bobberX} y2={pivotY + armatureLength} stroke="rgba(255, 255, 255, 0.5)" strokeWidth="1.5" data-id="element-536" />

          {/* Pause countdown to the left of bobber */}
          {state.pauseAtTop && state.pauseDuration > 0 && state.pauseCountdown > 0 && <text x={pivotX - 50} y={pivotY - 10} textAnchor="end" fill="#00ff88" fontSize="16" fontWeight="bold" style={{
          filter: 'drop-shadow(0 1px 3px rgba(0, 0, 0, 0.7))'
        }} data-id="element-537">
                ⏱ {state.pauseCountdown.toFixed(1)}s
              </text>}

          {/* ===== SWINGING ASSEMBLY (armature + line + bait) ===== */}
          {/* Rotates around the pivot point like a pendulum */}
          <g ref={swingGroupRef} data-id="element-538">
            {/* Armature — single straight bar */}
            <line ref={armatureLineRef} x1={pivotX} y1={pivotY} x2={pivotX} y2={armatureTipY} stroke="#ffffff" strokeWidth="3" strokeLinecap="round" style={{
            filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.5))'
          }} data-id="element-539" />
            {/* Small dot at armature tip */}
            <circle ref={armatureTipDotRef} cx={pivotX} cy={armatureTipY} r="3" fill="#4a4a4a" data-id="element-540" />

            {/* Fishing Line — curved path from armature tip to lure */}
            <path ref={fishingLineRef} d={`M ${pivotX} ${armatureTipY} L ${pivotX} ${baitY}`} stroke="rgba(255, 255, 255, 0.4)" strokeWidth="1.5" fill="none" data-id="element-541" />

            {/* Rubber Fish Lure — nose at origin, body extends behind (-X) */}
            <g ref={lureBodyRef} transform={`translate(${pivotX}, ${baitY})`} data-id="element-542">
              {/* === JIG HEAD — red lead head with hook === */}
              {/* Hook — runs horizontally through lure, barely exits top, curves back toward head */}
              <path d="M 0 1 L -18 1 L -18 -4 Q -18 -6.5 -15.5 -6.5 Q -13 -6.5 -13 -5.5" fill="none" stroke="#b0b0b0" strokeWidth="1.5" strokeLinecap="round" data-id="element-543" />
              {/* Hook barb */}
              <path d="M -13 -5.5 L -12.2 -6.8" fill="none" stroke="#b0b0b0" strokeWidth="1" strokeLinecap="round" data-id="element-544" />
              <path d="M -13 -5.5 L -14 -6.5" fill="none" stroke="#b0b0b0" strokeWidth="0.6" strokeLinecap="round" data-id="element-545" />
              {/* Hook eye — small ring where line attaches */}
              <circle cx="0" cy="-7" r="1.8" fill="none" stroke="#b0b0b0" strokeWidth="1.2" data-id="element-546" />
              {/* Hook eye stem */}
              <line x1="0" y1="-5.2" x2="0" y2="-3" stroke="#b0b0b0" strokeWidth="1.3" data-id="element-547" />
              {/* Red jig head — round weighted head */}
              <ellipse cx="0" cy="0" rx="5" ry="5.5" fill="#cc2200" data-id="element-548" />
              <ellipse cx="0" cy="0" rx="5" ry="5.5" fill="url(#jigHeadShading)" data-id="element-549" />
              {/* Head highlight — sheen */}
              <ellipse cx="-1" cy="-2" rx="3" ry="2.5" fill="#ff4422" opacity="0.4" data-id="element-550" />
              {/* Painted eye on jig head */}
              <circle cx="2" cy="-1.5" r="2" fill="#ffcc00" data-id="element-551" />
              <circle cx="2" cy="-1.5" r="1.2" fill="#1a1a1a" data-id="element-552" />
              <circle cx="2.3" cy="-2" r="0.4" fill="#ffffff" opacity="0.7" data-id="element-553" />

              {state.lureType === 'Shad' && <>
                  {/* Head at front */}
                  <ellipse cx="-3" cy="0" rx="4" ry="3.8" fill="#4a4a4a" data-id="element-554" />
                  {/* Mouth */}
                  <path d="M 0 0 L 1.5 -0.5 L 1.5 0.5 Z" fill="#1a1a1a" data-id="element-555" />

                  {/* Eye */}
                  <circle cx="-3" cy="-1.5" r="2" fill="#ffffff" data-id="element-556" />
                  <circle cx="-2.5" cy="-1.5" r="1.2" fill="#1a1a1a" data-id="element-557" />
                  <circle cx="-2" cy="-2" r="0.4" fill="#ffffff" data-id="element-558" />

                  {/* Body — animated flex path */}
                  <path ref={bodyPathRef} d="M 0 0 C -5 0 -14 0 -28 0 C -28 0 -14 0 0 0 Z" fill="#3a3a3a" opacity="0.15" data-id="element-559" />
                  {/* Body — solid base shape */}
                  <ellipse cx="-14" cy="0" rx="14" ry="4.7" fill={state.lureColor} data-id="element-560" />

                  {/* Animated tail section — single straight tail, no split */}
                  <g transform="translate(-28, 0)" data-id="element-561">
                    {/* Tail body taper — straight and narrow */}
                    <path d="M 0 -2.5 L -18 -1 L -18 1 L 0 2.5 Z" fill={state.lureColor} opacity="0.8" data-id="element-562" />
                    {/* Animated tail — single straight line with wave */}
                    <path ref={lureTailRef} d="M 0 0 L -9 0 L -18 0" fill="none" stroke={state.lureColor} strokeWidth="3" strokeLinecap="round" opacity="0.9" data-id="element-563" />
                    {/* Tail tip — rounded end cap (no fork/split) */}
                    <path ref={tailForkRef} d="M -18 -2 Q -20 0 -18 2" fill="none" stroke={state.lureColor} strokeWidth="2.5" strokeLinecap="round" opacity="0.8" data-id="element-564" />
                  </g>
                </>}

              {state.lureType === 'Swimbait' && <>
                  {/* === SWIMBAIT — Soft plastic paddle tail === */}

                  {/* Jig Head — bigger weighted head */}
                  <circle cx="3" cy="0" r="5" fill="#1a1a1a" data-id="element-565" />
                  <circle cx="4" cy="-1.5" r="2.5" fill="#4a4a4a" opacity="0.6" data-id="element-566" />

                  {/* Eye on jig head */}
                  <circle cx="5" cy="-0.5" r="1.5" fill="#ffffff" data-id="element-567" />
                  <circle cx="5.4" cy="-0.5" r="0.9" fill="#1a1a1a" data-id="element-568" />

                  {/* Body — 50% bigger, long tapered shape */}
                  <ellipse cx="-12" cy="0" rx="18" ry="5.25" fill={state.lureColor} data-id="element-569" />

                  {/* Body segments — ribbed texture */}
                  <line x1="-4" y1="-4.8" x2="-4" y2="4.8" stroke="#000000" strokeWidth="0.3" opacity="0.2" data-id="element-570" />
                  <line x1="-8" y1="-5.1" x2="-8" y2="5.1" stroke="#000000" strokeWidth="0.3" opacity="0.2" data-id="element-571" />
                  <line x1="-12" y1="-5.25" x2="-12" y2="5.25" stroke="#000000" strokeWidth="0.3" opacity="0.2" data-id="element-572" />
                  <line x1="-16" y1="-4.8" x2="-16" y2="4.8" stroke="#000000" strokeWidth="0.3" opacity="0.2" data-id="element-573" />
                  <line x1="-20" y1="-4.2" x2="-20" y2="4.2" stroke="#000000" strokeWidth="0.3" opacity="0.2" data-id="element-574" />
                  <line x1="-24" y1="-3.5" x2="-24" y2="3.5" stroke="#000000" strokeWidth="0.3" opacity="0.2" data-id="element-575" />

                  {/* Paddle tail — 50% bigger, same color as bait */}
                  <g transform="translate(-30, 0)" data-id="element-576">
                    <path ref={lureTailRef} d="M 0 -3.75 Q -6 -5.25 -12 -6 Q -18 -6.75 -24 -4.5 L -24 4.5 Q -18 6.75 -12 6 Q -6 5.25 0 3.75 Z" fill={state.lureColor} opacity="0.9" data-id="element-577" />
                    {/* Tail texture lines */}
                    <line x1="-6" y1="-5.25" x2="-6" y2="5.25" stroke="#000000" strokeWidth="0.2" opacity="0.15" data-id="element-578" />
                    <line x1="-12" y1="-6" x2="-12" y2="6" stroke="#000000" strokeWidth="0.2" opacity="0.15" data-id="element-579" />
                    <line x1="-18" y1="-6.3" x2="-18" y2="6.3" stroke="#000000" strokeWidth="0.2" opacity="0.15" data-id="element-580" />
                  </g>

                  {/* Belly highlight */}
                  <ellipse cx="-12" cy="2.25" rx="15" ry="2.25" fill="#ffffff" opacity="0.3" data-id="element-581" />
                </>}

              {state.lureType === 'WhiteJig' && <>
                  {/* Jig Head — weighted lead head */}
                  <ellipse cx="-3" cy="0" rx="4" ry="3.8" fill="#b0b0b0" data-id="element-582" />
                  {/* Jig head shading */}
                  <ellipse cx="-2.5" cy="-1" rx="3" ry="2.5" fill="#d0d0d0" opacity="0.6" data-id="element-583" />
                  {/* Mouth */}
                  <path d="M 0 0 L 1.5 -0.5 L 1.5 0.5 Z" fill="#808080" data-id="element-584" />

                  {/* Eye */}
                  <circle cx="-3" cy="-1.5" r="2" fill="#ffffff" data-id="element-585" />
                  <circle cx="-2.5" cy="-1.5" r="1.2" fill="#1a1a1a" data-id="element-586" />
                  <circle cx="-2" cy="-2" r="0.4" fill="#ffffff" data-id="element-587" />

                  {/* Body — animated flex path */}
                  <path ref={bodyPathRef} d="M 0 0 C -5 0 -14 0 -28 0 C -28 0 -14 0 0 0 Z" fill="#c0c0c0" opacity="0.15" data-id="element-588" />
                  {/* Body — solid base shape (white/pearl) */}
                  <ellipse cx="-14" cy="0" rx="14" ry="4.7" fill={state.lureColor} data-id="element-589" />
                  {/* Dorsal fin (animated ripple) */}
                  <path ref={dorsalRef} d="M -18 -5.5 Q -14 -10 -10 -5.5" fill={state.lureColor} opacity="0.8" data-id="element-590" />
                  {/* Anal fin */}
                  <path d="M -16 5.5 Q -13 9 -10 5.5" fill={state.lureColor} opacity="0.8" data-id="element-591" />

                  {/* Pectoral fins (animated flutter) */}
                  <path ref={pectoralTopRef} d="M -8 -4 Q -6 -8 -11 -7" fill={state.lureColor} opacity="0.8" data-id="element-592" />
                  <path ref={pectoralBotRef} d="M -8 4 Q -6 8 -11 7" fill={state.lureColor} opacity="0.8" data-id="element-593" />

                  {/* Tail section */}
                  <g transform="translate(-28, 0)" data-id="element-594">
                    {/* Tail body taper */}
                    <path d="M 0 -2.5 L -18 -1 L -18 1 L 0 2.5 Z" fill={state.lureColor} opacity="0.8" data-id="element-595" />
                    {/* Animated tail */}
                    <path ref={lureTailRef} d="M 0 0 L -9 0 L -18 0" fill="none" stroke={state.lureColor} strokeWidth="3" strokeLinecap="round" opacity="0.9" data-id="element-596" />
                    {/* Tail tip */}
                    <path ref={tailForkRef} d="M -18 -2 Q -20 0 -18 2" fill="none" stroke={state.lureColor} strokeWidth="1.5" strokeLinecap="round" opacity="0.7" data-id="element-597" />
                  </g>
                </>}

              {state.lureType === 'Minnow' && <>
                  {/* === ZOOM FLUKE — Soft Plastic Jerkbait === */}

                  {/* Pointed nose / head — slim taper */}
                  <path d="M 1 0 C 0 -1.5 -3 -2.5 -6 -3 L -6 3 C -3 2.5 0 1.5 1 0 Z" fill={state.lureColor} data-id="element-598" />
                  {/* Eye — small, dark */}
                  <circle cx="-2" cy="-0.8" r="1" fill="#2a2a2a" data-id="element-599" />
                  <circle cx="-1.7" cy="-1" r="0.3" fill="#ffffff" opacity="0.6" data-id="element-600" />

                  {/* Main body — long slim torpedo shape */}
                  <path ref={bodyPathRef} d="M -6 -3 C -10 -3.8 -20 -4 -30 -3.5 L -30 3.5 C -20 4 -10 3.8 -6 3 Z" fill={state.lureColor} data-id="element-601" />

                  {/* Tail taper section — narrows before split */}
                  <g transform="translate(-30, 0)" data-id="element-602">
                    <path d="M 0 -3.5 C -4 -3 -8 -2 -12 -1.5 L -12 1.5 C -8 2 -4 3 0 3.5 Z" fill={state.lureColor} opacity="0.9" data-id="element-603" />

                    {/* Split tail — signature fluke fork (animated) */}
                    <path ref={lureTailRef} d="M -12 -1.5 C -14 -2 -17 -4 -20 -6 C -18 -3 -16 -1 -12 0 C -16 1 -18 3 -20 6 C -17 4 -14 2 -12 1.5" fill={state.lureColor} opacity="0.85" data-id="element-604" />
                  </g>
                </>}

              {state.lureType === 'NedRig' && <>
                  {/* === NED RIG — Short worm on jig head === */}

                  {/* Jig Head — mushroom-shaped lead head */}
                  <ellipse cx="-2" cy="0" rx="3.5" ry="3.2" fill="#4a4a4a" data-id="element-605" />
                  {/* Jig head highlight */}
                  <ellipse cx="-1.5" cy="-0.8" rx="2.5" ry="2" fill="#707070" opacity="0.6" data-id="element-606" />

                  {/* Worm body — 15% longer */}
                  <ellipse cx="-14.9" cy="0" rx="13.8" ry="2.8" fill={state.lureColor} data-id="element-607" />

                  {/* Blunt tail end — no fork, just rounded */}
                  <ellipse cx="-27.2" cy="0" rx="1.5" ry="2.5" fill={state.lureColor} opacity="0.8" data-id="element-608" />
                </>}

              {/* Hook point */}
              <path d="M 0 0 C -1 2 -2 4 -3 3" fill="none" stroke="#8a8a8a" strokeWidth="0.8" strokeLinecap="round" data-id="element-609" />
            </g>
          </g>
        </svg>
      </div>
    </div>;
}